import { useNavigate } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const IMAGES = {
  hero: {
    src: "/images/smart-cooler-hero.800w.d28942cc.webp",
    srcSet: "/images/smart-cooler-hero.800w.d28942cc.webp 800w, /images/smart-cooler-hero.1600w.b38ec30e.webp 1600w",
    width: 800,
    height: 1066,
  },
  lounge: {
    src: "/images/smart-cooler-lounge.500w.c96b67a5.webp",
    srcSet: "/images/smart-cooler-lounge.500w.c96b67a5.webp 500w, /images/smart-cooler-lounge.1000w.9fe53323.webp 1000w",
    width: 500,
    height: 543,
  },
  interior: {
    src: "/images/smart-cooler-interior.600w.149051d1.webp",
    srcSet: "/images/smart-cooler-interior.600w.149051d1.webp 600w, /images/smart-cooler-interior.820w.3e09ab96.webp 820w",
    width: 600,
    height: 849,
  },
};

const AudiencePage = ({ overline, h1Lines, accentIndex, intro, howTitle, howText, included, faqs, imageKey, imageAlt, testId }) => {
  const navigate = useNavigate();
  const img = IMAGES[imageKey];
  return (
    <div data-testid={testId}>
      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-48 lg:pb-28">
        <div aria-hidden="true" className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-vortex-blue/15 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <MaskedLine delay={0.1}>
                <Overline>{overline}</Overline>
              </MaskedLine>
              <h1 className="mt-8 font-heading font-black tracking-tighter leading-[1.0] text-4xl sm:text-5xl lg:text-6xl" data-testid={`${testId}-heading`}>
                {h1Lines.map((line, i) => (
                  <MaskedLine key={i} delay={0.25 + i * 0.15}>
                    {i === accentIndex ? <span className="text-vortex-cyan">{line}</span> : line}
                  </MaskedLine>
                ))}
              </h1>
              <Reveal delay={0.6}>
                <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">{intro}</p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <a
                    data-testid={`${testId}-call-link`}
                    href="tel:+16109838060"
                    className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(92,179,226,0.5)]"
                  >
                    <Phone size={15} />
                    Call (610) 983-8060
                  </a>
                  <button
                    data-testid={`${testId}-contact-button`}
                    onClick={() => navigate("/contact")}
                    className="group flex items-center gap-3 rounded-full border border-white/15 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-slate-300 transition-all duration-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan"
                  >
                    Send a message
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.5}>
                <div className="clip-frame overflow-hidden border border-white/10 glow-cyan">
                  <img
                    src={img.src}
                    srcSet={img.srcSet}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    width={img.width}
                    height={img.height}
                    loading="lazy"
                    alt={imageAlt}
                    className="h-80 w-full object-cover lg:h-[30rem]"
                    data-testid={`${testId}-image`}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-vortex-surface/20">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <Overline>How It Works</Overline>
              <h2 className="mt-5 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">{howTitle}</h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-8">
              <p className="max-w-2xl text-base leading-relaxed text-slate-400">{howText}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>What's Included</Overline>
          </Reveal>
          <div className="mt-10">
            {included.map(([title, body], i) => (
              <Reveal key={title} delay={0.05 * i}>
                <div className="group flex flex-col gap-3 border-t border-white/10 py-7 transition-colors duration-500 hover:border-vortex-cyan/30 sm:flex-row sm:items-start sm:gap-10" data-testid={`${testId}-included-${i}`}>
                  <span className="font-mono text-sm tracking-[0.25em] text-vortex-cyan">{`0${i + 1}`}</span>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold tracking-tight text-white transition-transform duration-500 group-hover:translate-x-2 sm:text-xl">{title}</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-white/10" />
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-vortex-surface/20">
        <div className="mx-auto max-w-4xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>Common Questions</Overline>
            <h2 className="mt-5 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">Asked and answered.</h2>
            <Accordion type="single" collapsible className="mt-10" data-testid={`${testId}-faqs`}>
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`${testId}-faq-${i}`} className="text-left font-heading text-lg font-bold tracking-tight text-white hover:text-vortex-cyan hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[24rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-vortex-blue/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
          <Reveal>
            <h2 className="max-w-3xl font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
              TALK TO A <span className="text-vortex-cyan">REAL PERSON.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base text-slate-400">
              Call us Monday–Friday, 8:00 AM – 8:00 PM, or send a message any time.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                data-testid={`${testId}-cta-call`}
                href="tel:+16109838060"
                className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_50px_rgba(92,179,226,0.5)]"
              >
                <Phone size={16} />
                (610) 983-8060
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AudiencePage;
